import { useEffect, useRef, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown,
} from "lucide-react";
import { useProjectContext } from "../contexts/ProjectContext";
import { useNotifications } from "../contexts/NotificationContext";

import { useAuth } from "../hooks/useAuth";
import { useTemplates } from "../hooks/useTemplates";
import { useIntegrations } from "../hooks/useIntegrations"; // Import useIntegrations
// useClickOutside is now handled within useProjectMenuState
// Dialog imports are moved to ProjectMenuDialogs, except AuthRequiredModal
// import TemplateDialog from "./dialogs/TemplateDialog";
// import SaveTemplateDialog from "./dialogs/SaveTemplateDialog";
import AuthRequiredModal from "./dialogs/AuthRequiredModal"; // Keep this one
// import RenameDialog from "./dialogs/RenameDialog";
// import { DuplicateProjectDialog } from "./dialogs/DuplicateProjectDialog";
// import ConfirmDialog from "./dialogs/ConfirmDialog";
// import LoadProjectDialog from "./dialogs/LoadProjectDialog";
// import ShareProjectDialog from "./dialogs/ShareProjectDialog";
import useStore from "../store/useStore";
import { useProjectMenuState } from "../hooks/useProjectMenuState"; // Import the state hook
import { useProjectMenuHandlers } from "../hooks/useProjectMenuHandlers"; // Import the handlers hook
import ProjectMenuItems from "./ProjectMenuItems"; // Import the menu items component
import ProjectMenuDialogs from "./ProjectMenuDialogs";


export default function ProjectMenu() {
  const fileInputRef = useRef<HTMLInputElement>(null); // For JSON import
  const csvFileInputRef = useRef<HTMLInputElement>(null); // For CSV import

  // Use the custom hook for state management
  const {
    isOpen, setIsOpen,
    showTemplateDialog, setShowTemplateDialog,
    showSaveTemplateDialog, setShowSaveTemplateDialog,
    showRenameDialog, setShowRenameDialog,
    showCloseProjectDialog, setShowCloseProjectDialog,
    showDeleteProjectDialog, setShowDeleteProjectDialog,

    showSaveAsDialog, setShowSaveAsDialog, // Added showSaveAsDialog, setShowSaveAsDialog
    showLoadProjectDialog, setShowLoadProjectDialog,
    showTemplateMenu, setShowTemplateMenu,
    showAuthRequiredModal, setShowAuthRequiredModal,
    showShareDialog, setShowShareDialog,
    showNewProjectConfirmDialog, setShowNewProjectConfirmDialog, // Added confirm dialog state
    showKeyboardShortcutsDialog, setShowKeyboardShortcutsDialog, // Added keyboard shortcuts dialog state
    menuRef // Get menuRef from the hook
  } = useProjectMenuState();

  // Get setNodes/setEdges first
  const { setNodes, setEdges } = useStore(); // Get setNodes/setEdges here to pass to the hook

  // Get handlers from the custom hook, passing setNodes/setEdges
  const {
    handleSave,
    handleSaveAs,
    handleDelete,
    handleNewProject, // This now triggers the check/dialog
    proceedWithNewProject, // Added handler for dialog confirm
    saveAndProceedWithNewProject, // Added handler for dialog save option
    handleTemplateSelect,
    handleExportProject,
    handleImportProject,
    handleImportAuditCsv,
    handleFileChange,
    handleCsvFileChange,
    handleClearCanvas,
  } = useProjectMenuHandlers({
    setIsOpen,
    setShowAuthRequiredModal,
    setShowSaveAsDialog,
    setShowTemplateMenu,
    setShowNewProjectConfirmDialog, // Added confirm dialog setter
    fileInputRef,
    csvFileInputRef,
    setNodes, // Pass setNodes
    setEdges, // Pass setEdges
  });

  // Still need these contexts/hooks for data used directly in JSX or useEffects
  const { loadIntegrations: loadIntegrationsHook } = useIntegrations();
  const { project, isPreviewMode } = useProjectContext();
  const { user } = useAuth();
  const { showNotification } = useNotifications(); // Keep for direct use in JSX onClick
  const { templates, loadTemplates } = useTemplates();
  const { requiresAuth } = useAuth(); // Get requiresAuth for shortcut logic
  // setNodes and setEdges are now obtained above

  // Load integrations and templates on mount
  useEffect(() => {
    loadIntegrationsHook().catch(console.error);
    loadTemplates();
  }, [loadIntegrationsHook, loadTemplates]);

  // Define isNotOwner before useEffect that uses it
  const isUnsavedProject = project?.id === "unsaved-project";
  const isNotOwner = useMemo(() => !project || !user ? false : project.owner_id !== user.id, [project, user]);

  // Add useEffect for keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if running in a browser environment before accessing navigator
      const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? event.metaKey : event.ctrlKey;

      // Prevent shortcuts if an input/textarea is focused or a dialog is likely open
      const activeElement = document.activeElement;
      const isInputFocused = activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement;
      // Simple check for dialogs - includes all dialog states from useProjectMenuState
      // Added showSaveAsDialog check
      const isDialogLikelyOpen = showTemplateDialog || showSaveTemplateDialog || showRenameDialog || showCloseProjectDialog || showDeleteProjectDialog || showSaveAsDialog || showLoadProjectDialog || showAuthRequiredModal || showShareDialog || showKeyboardShortcutsDialog; 

      if (isInputFocused || isDialogLikelyOpen || isOpen) { // Also prevent if menu itself is open
        return;
      }

      if (modifierKey) {
        switch (event.code) { // Changed from event.key.toLowerCase() to event.code
          case 'KeyN': // Was 'n'
            if (event.altKey) { // Require Alt/Option key
              event.preventDefault(); // Prevent browser's default new window action
              handleNewProject();
            }
            break;
          case 'KeyS': // Was 's'; Handles Cmd/Ctrl+S, Cmd/Ctrl+Shift+S, Cmd/Ctrl+Alt+S
            if (event.altKey) { // Cmd/Ctrl+Option/Alt+S for Save as Template
              if (!requiresAuth('admin') && !isPreviewMode && project && project.id !== "unsaved-project" && !isNotOwner) {
                event.preventDefault();
                setShowSaveTemplateDialog(true);
                setIsOpen(false); // Close menu if open
              }
            } else if (event.shiftKey) { // Cmd/Ctrl+Shift+S for Save As
              // Updated condition: user must be authenticated, project loaded and saved, and not in preview mode
              if (user && project && project.id !== "unsaved-project" && !isPreviewMode) {
                event.preventDefault();
                handleSaveAs();
              }
            } else { // Cmd/Ctrl+S for Save
              if (!isPreviewMode && !isNotOwner && !(requiresAuth('save') && project?.id === "unsaved-project")) {
                event.preventDefault();
                handleSave();
              }
            }
            break;
          case 'KeyO': // Was 'o'
             // Prevent load if requires auth ('save' permission implies loading ability here)
            if (!requiresAuth('save')) {
              event.preventDefault(); // Prevent browser's default open file action
              setShowLoadProjectDialog(true);
            }
            break;
          case 'KeyE': // Was 'e'; Export: Cmd/Ctrl+E
            if (!isPreviewMode && project && project.id !== "unsaved-project" && !isNotOwner && !requiresAuth('admin')) { // Assuming export is not admin restricted by default
              event.preventDefault();
              handleExportProject();
            }
            break;
          case 'KeyI': // Was 'i'; Import: Cmd/Ctrl+I and Audit Import: Cmd/Ctrl+Shift+I
            if (event.shiftKey) { // Audit Import: Cmd/Ctrl+Shift+I
              if (!requiresAuth('admin')) { // Assuming admin for audit import
                event.preventDefault();
                handleImportAuditCsv();
              }
            } else { // Regular Import: Cmd/Ctrl+I
              if (!requiresAuth('admin')) { // Assuming admin for regular import
                event.preventDefault();
                handleImportProject();
              }
            }
            break;
          // Note: The 's' case above now handles Cmd/Ctrl+Shift+S for Save As.
          // The original separate 's' case for Shift+S has been merged.
          default:
            break;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    // Cleanup function
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    // Handlers
    handleNewProject,
    handleSave,
    handleSaveAs, // Added handleSaveAs
    handleExportProject, // Added for shortcut
    handleImportProject, // Added for shortcut
    handleImportAuditCsv, // Added for shortcut
    // State setters & values needed for logic
    setShowLoadProjectDialog,
    setShowSaveTemplateDialog, // Added for Save as Template shortcut
    isPreviewMode,
    isNotOwner,
    project, // Need project to check its ID for save shortcut condition
    requiresAuth,
    isOpen, // Need menu open state
    // Dialog states to prevent shortcuts when dialogs are open
    showTemplateDialog,
    showSaveTemplateDialog,
    showRenameDialog,
    showCloseProjectDialog,
    showDeleteProjectDialog,
    showSaveAsDialog,
    showLoadProjectDialog,
    showAuthRequiredModal,
    showShareDialog,
    showNewProjectConfirmDialog, // Added confirm dialog state to dependencies
    showKeyboardShortcutsDialog, // Added keyboard shortcuts dialog state to dependencies
  ]);


  // Handler for successful template save
  const handleSaveTemplateSuccess = () => {
    loadTemplates(); // Call loadTemplates to refresh the list
  };

  // --- Handlers are now imported from useProjectMenuHandlers ---

  // --- Render ---
  // JSX remains largely the same, but onClick/onChange handlers now reference
  // the functions returned by useProjectMenuHandlers
  return (
    <>
      {showAuthRequiredModal && <AuthRequiredModal onClose={() => setShowAuthRequiredModal(false)} />}
      <div className="relative">
        {/* Hidden file input for JSON import */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".json"
          className="hidden"
        />
        {/* Hidden file input for CSV import */}
        <input
          type="file"
          ref={csvFileInputRef}
          onChange={handleCsvFileChange}
          accept=".csv"
          className="hidden"
        />
        <motion.button
          whileHover={{ backgroundColor: "rgba(220, 220, 220, 0.1)" }}
          transition={{ duration: 0.1 }}
          onClick={() => setIsOpen(!isOpen)}
          className={`inline-flex items-center gap-2 px-4 py-2 text-sm text-white/90 hover:text-white hover:bg-white/10 rounded-md transition-all ${isOpen ? "bg-white/10" : ""} min-w-[150px] max-w-[300px]`}
          whileTap={{ scale: 0.98 }}
        >
          <span className="truncate">{project?.name || "Untitled Project"}</span>
          <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.1, type: "ease", stiffness: 300 }}>
            <ChevronDown className="h-4 w-4" />
          </motion.div>
        </motion.button>

        {isOpen && <div className="fixed inset-0 bg-gray-900/0 z-40" onClick={() => setIsOpen(false)} />}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={menuRef}
              className="absolute top-full left-0 mt-1 w-64 rounded-md shadow-lg bg-gray-900 border border-gray-800 dark:bg-gray-950 ring-1 ring-black ring-opacity-5 z-50"
              initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.1 }}
            >
              {/* Render the extracted menu items component */}
              <ProjectMenuItems
                project={project}
                templates={templates}
                isPreviewMode={isPreviewMode}
                isUnsavedProject={isUnsavedProject}
                isNotOwner={isNotOwner}
                showTemplateMenu={showTemplateMenu}
                handleNewProject={handleNewProject}
                handleSave={handleSave}
                handleTemplateSelect={handleTemplateSelect}
                handleExportProject={handleExportProject}
                handleImportProject={handleImportProject}
                handleImportAuditCsv={handleImportAuditCsv}
                handleClearCanvas={handleClearCanvas}
                setIsOpen={setIsOpen}
                setShowLoadProjectDialog={setShowLoadProjectDialog}
                setShowRenameDialog={setShowRenameDialog}
                setShowSaveAsDialog={setShowSaveAsDialog} // Changed from setShowDuplicateDialog
                setShowDeleteProjectDialog={setShowDeleteProjectDialog}
                setShowTemplateMenu={setShowTemplateMenu}
                setShowTemplateDialog={setShowTemplateDialog}
                setShowSaveTemplateDialog={setShowSaveTemplateDialog}
                setShowShareDialog={setShowShareDialog}
                showNotification={showNotification} // Pass showNotification
              />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Render the extracted dialogs component */}
        <ProjectMenuDialogs
          project={project}
          showAuthRequiredModal={showAuthRequiredModal} // Pass this down, but AuthRequiredModal is still rendered outside
          showTemplateDialog={showTemplateDialog}
          showSaveTemplateDialog={showSaveTemplateDialog}
          showLoadProjectDialog={showLoadProjectDialog}
          showRenameDialog={showRenameDialog}
          showSaveAsDialog={showSaveAsDialog}
          showCloseProjectDialog={showCloseProjectDialog}
          showDeleteProjectDialog={showDeleteProjectDialog}
          showShareDialog={showShareDialog}
          showNewProjectConfirmDialog={showNewProjectConfirmDialog} // Pass down confirm dialog state
          showKeyboardShortcutsDialog={showKeyboardShortcutsDialog} // Pass down keyboard shortcuts dialog state
          setShowAuthRequiredModal={setShowAuthRequiredModal}
          setShowTemplateDialog={setShowTemplateDialog}
          setShowSaveTemplateDialog={setShowSaveTemplateDialog}
          setShowLoadProjectDialog={setShowLoadProjectDialog}
          setShowRenameDialog={setShowRenameDialog}
          setShowSaveAsDialog={setShowSaveAsDialog}
          setShowCloseProjectDialog={setShowCloseProjectDialog}
          setShowDeleteProjectDialog={setShowDeleteProjectDialog}
          setShowShareDialog={setShowShareDialog}
          setShowNewProjectConfirmDialog={setShowNewProjectConfirmDialog} // Pass down confirm dialog setter
          setShowKeyboardShortcutsDialog={setShowKeyboardShortcutsDialog} // Pass down keyboard shortcuts dialog setter
          // Pass down handlers for confirm dialog
          handleNewProject={handleNewProject} // Still used by old CloseProjectDialog
          handleDelete={handleDelete}
          proceedWithNewProject={proceedWithNewProject} // Pass down handler
          saveAndProceedWithNewProject={saveAndProceedWithNewProject} // Pass down handler
          onSaveSuccess={handleSaveTemplateSuccess} // Pass the success handler
        />
      </div>
    </>
  );
}
